import { RefObject, useCallback, useRef, useState } from "react";
import { message } from "antd";

import { exportElementToPdf } from "utils/page-pdf";

// Capturing a long report takes a moment, so progress goes in a toast.
const PROGRESS_MESSAGE_KEY = "pdf-export";

interface PagePdfExport<T extends HTMLElement> {
	/** Attach to the element to export — its subtree is what ends up in the PDF. */
	exportRef: RefObject<T>;
	/**
	 * True from the click until the file is saved. Pages use it to open every collapsible
	 * section first, so the capture carries the whole report.
	 */
	isExporting: boolean;
	exportToPdf: (fileName: string, renderWidthPx?: number) => Promise<void>;
}

/**
 * "Export" on a report page: saves the referenced element as a PDF of the page itself —
 * same DOM, same stylesheet — so the file follows the screen without a second layout to
 * maintain. See utils/page-pdf for how the capture works, and mark anything that should
 * stay out of the file (buttons, navigation) with `data-pdf-hide`.
 */
const usePagePdfExport = <T extends HTMLElement = HTMLDivElement>(): PagePdfExport<T> => {
	const exportRef = useRef<T>(null);
	const [isExporting, setIsExporting] = useState(false);

	const exportToPdf = useCallback(async (fileName: string, renderWidthPx?: number) => {
		setIsExporting(true);
		message.loading({ content: "Preparing the PDF…", key: PROGRESS_MESSAGE_KEY, duration: 0 });

		// Let the sections the page opens for the export paint before cloning them.
		await new Promise<void>(resolve =>
			window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()))
		);

		const element = exportRef.current;
		if (!element) {
			message.destroy(PROGRESS_MESSAGE_KEY);
			setIsExporting(false);
			return;
		}

		try {
			await exportElementToPdf(element, fileName, renderWidthPx);
			message.success({ content: "Report exported.", key: PROGRESS_MESSAGE_KEY, duration: 2 });
		} catch (error) {
			console.error("PDF export failed:", error);
			message.error({ content: "Couldn't export the report. Please try again.", key: PROGRESS_MESSAGE_KEY });
		} finally {
			setIsExporting(false);
		}
	}, []);

	return { exportRef, isExporting, exportToPdf };
};

export default usePagePdfExport;
